import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { SupplierVm } from 'api/reception/supplier.vm';
import { GetSuppliersVm } from 'api/reception/get-suppliers.vm';
import { getSuppliers } from 'api/reception/get-suppliers';

const SuppliersPage = () => {
  const theme = useTheme();
  const [suppliers, setSuppliers] = useState<SupplierVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const fetchSuppliers = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getSuppliers(offset || '', appliedSearchTerm, {
      success: (response: GetSuppliersVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setSuppliers(reset ? response.objects : [...suppliers, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter fornecedores:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchSuppliers(null, true);
  }, [appliedSearchTerm]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchSuppliers(nextOffset);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Fornecedores
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Procurar fornecedores por nome..."
            InputProps={{
              startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
            }}
            sx={{ width: 300 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          />
          <Button variant="contained" onClick={handleSearchSubmit} disabled={loading}>
            Procurar
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id} hover>
                <TableCell>{supplier.id}</TableCell>
                <TableCell>{supplier.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : suppliers.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais fornecedores para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {appliedSearchTerm ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor disponível'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SuppliersPage;
